import { useEffect, useMemo, useRef, useState } from 'react';
import type { CounterpartyFinance, ExpenseItem, FinanceAccount, FinancePaymentResult, PayPart } from '@kassa/shared';
import { api, ApiError } from '../api';
import { formatDate } from '../format';
import { PageHeader } from '../components/ListPage';

// Maliyyə → «Yeni ödəniş» (KT-102). Tablar: qaimə üzrə, xərc, hesablar arası. Qaimə üzrə: qarşı tərəf seçiləndə borclar
// (bizə borcu, bizim borcumuz, avans) və açıq qaimələr həmin səhifədə; çoxlu seçim, qismən məbləğ, «Sıra ilə ödə».
// Hesab tam qəpiklə serverdədir; burada yalnız göstərmə (məbləğ sətir kimi gedir).

type Tab = 'invoice' | 'expense' | 'transfer';
const PARTS: [PayPart, string][] = [['both', 'Hər ikisi'], ['invoice', 'Qaimə hissəsi'], ['vat', 'ƏDV hissəsi']];
const METHOD_LABEL = { cash: 'Nağd', transfer: 'Bank (köçürmə)', card: 'Kart' } as const;
type Method = keyof typeof METHOD_LABEL;

function errText(err: unknown): string {
  return err instanceof ApiError ? err.message : 'Şəbəkə xətası. Yenidən cəhd edin.';
}

/** «12,5» / «12.50» → «12.50»; düzgün deyilsə null. Göstərmə və serverə sətir (float hesab yox). */
function normAzn(v: string): string | null {
  const s = v.trim().replace(',', '.');
  return /^\d{1,12}(\.\d{1,2})?$/.test(s) ? s : null;
}

function money(s: string): string {
  const [i, f = '00'] = s.replace('-', '').split('.');
  return `${s.startsWith('-') ? '−' : ''}${i.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${f.padEnd(2, '0')} ₼`;
}

export function FinancePayment() {
  const [tab, setTab] = useState<Tab>('invoice');
  const [accounts, setAccounts] = useState<FinanceAccount[]>([]);
  const [parties, setParties] = useState<{ id: string; name: string; kind: 'customer' | 'supplier' }[]>([]);
  const [items, setItems] = useState<ExpenseItem[]>([]);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [attempts, setAttempts] = useState<Record<string, string>>({});

  // Qaimə üzrə
  const [direction, setDirection] = useState<'in' | 'out'>('in');
  const [partyId, setPartyId] = useState('');
  const [fin, setFin] = useState<CounterpartyFinance | null>(null);
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [amount, setAmount] = useState('');
  const [part, setPart] = useState<PayPart>('both');
  // Ümumi
  const [method, setMethod] = useState<Method>('cash');
  const [accountId, setAccountId] = useState('');
  const [note, setNote] = useState('');
  const [result, setResult] = useState<FinancePaymentResult | null>(null);
  // Xərc və hesablar arası
  const [expenseItemId, setExpenseItemId] = useState('');
  const [toAccountId, setToAccountId] = useState('');
  // Hesablar arası mənbə — ödəniş növündən asılı deyil (bank → kassa da olur)
  const [fromAccountId, setFromAccountId] = useState('');

  useEffect(() => {
    api.financeAccounts().then((a) => setAccounts(a.filter((x) => x.active))).catch((err) => setMsg({ ok: false, text: errText(err) }));
    api.expenseItems().then((x) => setItems(x.filter((i) => i.active))).catch(() => setItems([]));
    Promise.all([
      api.customers({ limit: 100 }).then((r) => r.items.map((c) => ({ id: c.id, name: c.name, kind: 'customer' as const }))).catch(() => []),
      api.purchaseOptions().then((o) => o.suppliers.map((s) => ({ id: s.id, name: s.name, kind: 'supplier' as const }))).catch(() => []),
    ]).then(([c, s]) => setParties([...c, ...s]));
  }, []);

  // Qarşı tərəfin borcları; köhnə cavab yenisinin üstünə yazılmasın (sıra nömrəsi)
  const seq = useRef(0);
  const loadParty = (id: string) => {
    const n = ++seq.current;
    setFin(null);
    setSelected({});
    if (!id) return;
    api
      .financeCounterparty(id)
      .then((f) => n === seq.current && setFin(f))
      .catch((err) => n === seq.current && setMsg({ ok: false, text: errText(err) }));
  };
  useEffect(() => {
    setResult(null);
    loadParty(partyId);
  }, [partyId]);

  // Ödəniş növünə uyğun hesablar (nağd — kassa, köçürmə/kart — bank)
  const usable = useMemo(() => accounts.filter((a) => (method === 'cash' ? a.kind === 'cash' : a.kind !== 'cash')), [accounts, method]);
  useEffect(() => {
    if (!usable.some((a) => a.id === accountId)) setAccountId(usable[0]?.id ?? '');
  }, [usable, accountId]);

  const invoices = fin ? (direction === 'in' ? fin.receivable : fin.payable) : [];
  const manual = Object.keys(selected).length > 0;

  async function run(key: string, fn: (id: string) => Promise<string>) {
    const id = attempts[key] ?? crypto.randomUUID();
    setAttempts((a) => ({ ...a, [key]: id }));
    setBusy(true);
    setMsg(null);
    try {
      setMsg({ ok: true, text: await fn(id) });
      setAttempts((a) => {
        const next = { ...a };
        delete next[key];
        return next;
      });
    } catch (err) {
      setMsg({ ok: false, text: errText(err) });
    } finally {
      setBusy(false);
    }
  }

  function pay() {
    if (!fin || !accountId) return setMsg({ ok: false, text: 'Qarşı tərəfi və hesabı seçin' });
    const its = Object.entries(selected).map(([docId, v]) => ({ docId, amount: normAzn(v) }));
    if (its.some((i) => !i.amount)) return setMsg({ ok: false, text: 'Seçilmiş qaimələrin məbləğini düzgün yazın' });
    const total = manual ? null : normAzn(amount);
    if (!manual && !total) return setMsg({ ok: false, text: 'Məbləğ yazın (məs. 120.50)' });
    void run(`pay:${partyId}:${direction}:${method}:${accountId}:${part}:${total ?? JSON.stringify(its)}`, async (id) => {
      const r = await api.financePay({
        // Nağdda ƏDV depozitə getmir — hissə həmişə «hər ikisi» (server də yoxlayır)
        id, counterpartyId: fin.counterpartyId, direction, method, moneyAccountId: accountId, part: direction === 'out' ? (method === 'cash' ? 'both' : part) : 'invoice',
        amount: total ?? undefined, items: manual ? (its as { docId: string; amount: string }[]) : undefined, note: note || undefined,
      });
      setResult(r);
      setAmount('');
      loadParty(partyId);
      return r.duplicate ? 'Bu ödəniş artıq yazılıb (təkrar göndərmə).' : ['Ödəniş yazıldı.', ...r.warnings].join(' ');
    });
  }

  function fromAdvance() {
    if (!fin) return;
    void run(`adv:${partyId}`, async (id) => {
      const r = await api.financeAdvance({ id, counterpartyId: fin.counterpartyId, part });
      setResult(r);
      loadParty(partyId);
      return `Avansdan ödəndi. Qalan avans ${money(r.advance)}.`;
    });
  }

  function expense() {
    const a = normAzn(amount);
    if (!a || !expenseItemId || !accountId) return setMsg({ ok: false, text: 'Xərc maddəsi, hesab və məbləğ lazımdır' });
    void run(`expense:${expenseItemId}:${accountId}:${method}:${a}`, async (id) => {
      await api.financeExpense({ id, expenseItemId, moneyAccountId: accountId, method, amount: a, note: note || undefined });
      setAmount('');
      return 'Xərc yazıldı.';
    });
  }

  function transfer() {
    const a = normAzn(amount);
    if (!a || !fromAccountId || !toAccountId) return setMsg({ ok: false, text: 'Hər iki hesabı və məbləği seçin' });
    void run(`transfer:${fromAccountId}:${toAccountId}:${a}`, async (id) => {
      await api.financeAccountTransfer({ id, fromAccountId, toAccountId, amount: a, note: note || undefined });
      setAmount('');
      return 'Hesablar arası köçürmə yazıldı.';
    });
  }

  const accountSelect = (value: string, onChange: (v: string) => void, list: FinanceAccount[], label: string) => (
    <label>
      {label}
      <select value={value} onChange={(e) => onChange(e.target.value)} disabled={busy}>
        <option value="">Seçin</option>
        {list.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name}
            {a.registerName ? ` (kassa ${a.registerName})` : ''} — {money(a.balance)}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <main className="page page-list">
      <PageHeader
        title="Yeni ödəniş"
        tabs={
          <div role="tablist" aria-label="Ödənişin növü" className="head-tabs">
            {([['invoice', 'Qaimə üzrə'], ['expense', 'Xərc'], ['transfer', 'Hesablar arası']] as [Tab, string][]).map(([k, l]) => (
              <button
                key={k}
                type="button"
                role="tab"
                id={`tab-${k}`}
                aria-controls="pay-panel"
                aria-selected={tab === k}
                tabIndex={tab === k ? 0 : -1}
                className="head-tab"
                onClick={() => {
                  setTab(k);
                  setMsg(null);
                }}
              >
                {l}
              </button>
            ))}
          </div>
        }
      />
      {msg && <p className={msg.ok ? 'ok' : 'bad'} role="status">{msg.text}</p>}

      <div className="pay-layout">
        <section className="card pay-main" id="pay-panel" role="tabpanel" aria-labelledby={`tab-${tab}`}>
          {tab === 'invoice' && (
            <>
              <div className="form-grid">
                <label>
                  İstiqamət
                  <select value={direction} onChange={(e) => { setDirection(e.target.value as 'in' | 'out'); setPartyId(''); }} disabled={busy}>
                    <option value="in">Mədaxil (müştəridən)</option>
                    <option value="out">Məxaric (təchizatçıya)</option>
                  </select>
                </label>
                <label>
                  Qarşı tərəf
                  <select value={partyId} onChange={(e) => setPartyId(e.target.value)} disabled={busy}>
                    <option value="">Seçin</option>
                    {parties.filter((p) => (direction === 'in' ? p.kind === 'customer' : p.kind === 'supplier')).map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </label>
              </div>
              {fin && (
                <div className="pay-summary" aria-label="Borclar">
                  <span>Bizə borcu: <strong>{money(fin.owesUs)}</strong></span>
                  <span>Bizim borcumuz: <strong>{money(fin.weOwe)}</strong></span>
                  <span>Avans: <strong>{money(fin.advance)}</strong></span>
                </div>
              )}
              {fin?.mismatch && (
                <p className="bad" role="alert">
                  Bu qarşı tərəfin hərəkətləri balansla üst-üstə düşmür — məbləğləri mühasib yoxlasın. Avansdan ödəmə bağlıdır.
                </p>
              )}
              {fin && invoices.length === 0 && <p className="muted">Açıq qaimə yoxdur{direction === 'in' ? '' : ''}.</p>}
              {invoices.length > 0 && (
                <div className="table-scroll">
                  <table className="report-table" aria-label="Açıq qaimələr">
                    <thead>
                      <tr>
                        <th />
                        <th>Qaimə</th>
                        <th className="num">Açıq</th>
                        {direction === 'out' && <th className="num">ƏDV hissəsi</th>}
                        <th className="num">Ödənilir</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((i) => {
                        const on = i.docId in selected;
                        return (
                          <tr key={i.docId}>
                            <td>
                              <input
                                type="checkbox"
                                aria-label={`${i.displayNo} seç`}
                                checked={on}
                                disabled={busy || direction === 'in'}
                                onChange={(e) =>
                                  setSelected((s) => {
                                    const n = { ...s };
                                    if (e.target.checked) n[i.docId] = part === 'vat' ? i.openVat : part === 'invoice' ? i.openNet : i.open;
                                    else delete n[i.docId];
                                    return n;
                                  })
                                }
                              />
                            </td>
                            <td>
                              {i.displayNo}
                              <div className="muted small">{formatDate(i.date)}</div>
                            </td>
                            <td className="num">{money(i.open)}</td>
                            {direction === 'out' && <td className="num">{money(i.openVat)}</td>}
                            <td className="num">
                              {on ? (
                                <input
                                  className="num-input"
                                  inputMode="decimal"
                                  aria-label={`${i.displayNo} — ödənilən məbləğ`}
                                  value={selected[i.docId]}
                                  onChange={(e) => setSelected((s) => ({ ...s, [i.docId]: e.target.value }))}
                                  disabled={busy}
                                />
                              ) : (
                                '—'
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {direction === 'in' && fin && <p className="muted small">Müştəri ödənişi ən köhnə borcdan başlayaraq bağlanır.</p>}
              {direction === 'out' && fin && (
                <fieldset className="fieldset">
                  <legend>Nə ödənilir</legend>
                  {PARTS.map(([k, l]) => (
                    <label key={k} className="check">
                      <input
                        type="radio"
                        name="part"
                        checked={(method === 'cash' ? 'both' : part) === k}
                        onChange={() => {
                          setPart(k);
                          // Seçilmiş qaimələrin məbləği yeni hissənin açıq qalığı ilə yenilənir
                          setSelected((sel) =>
                            Object.fromEntries(
                              Object.keys(sel).map((id) => {
                                const i = invoices.find((x) => x.docId === id);
                                return [id, i ? (k === 'vat' ? i.openVat : k === 'invoice' ? i.openNet : i.open) : sel[id]];
                              }),
                            ),
                          );
                        }}
                        disabled={busy || method === 'cash'}
                      />{' '}
                      {l}
                    </label>
                  ))}
                </fieldset>
              )}
              {fin && !manual && (
                <label>
                  Məbləğ — «Sıra ilə ödə» (ən köhnə qaimədən; artığı avansa)
                  <input inputMode="decimal" value={amount} placeholder="0.00" onChange={(e) => setAmount(e.target.value)} disabled={busy} />
                </label>
              )}
              {result && result.allocations.length > 0 && (
                <ul className="plain-list small" aria-label="Bölgü">
                  {result.allocations.map((a) => (
                    <li key={a.docId}>
                      {a.displayNo}: {money(a.net)}
                      {a.vat !== '0.00' && ` + ƏDV ${money(a.vat)}`}
                    </li>
                  ))}
                  {result.advance !== '0.00' && <li>Avansa: {money(result.advance)}</li>}
                </ul>
              )}
            </>
          )}

          {tab === 'expense' && (
            <div className="form-grid">
              <label>
                Xərc maddəsi
                <select value={expenseItemId} onChange={(e) => setExpenseItemId(e.target.value)} disabled={busy}>
                  <option value="">Seçin</option>
                  {items.map((i) => (
                    <option key={i.id} value={i.id}>{i.name}</option>
                  ))}
                </select>
              </label>
              <label>
                Məbləğ
                <input inputMode="decimal" value={amount} placeholder="0.00" onChange={(e) => setAmount(e.target.value)} disabled={busy} />
              </label>
              {items.length === 0 && <p className="muted small">Xərc maddəsi yoxdur — «Hesablar» bölməsində əlavə edin.</p>}
            </div>
          )}

          {tab === 'transfer' && (
            <div className="form-grid">
              {accountSelect(fromAccountId, setFromAccountId, accounts, 'Haradan')}
              {accountSelect(toAccountId, setToAccountId, accounts.filter((a) => a.id !== fromAccountId), 'Haraya')}
              <label>
                Məbləğ
                <input inputMode="decimal" value={amount} placeholder="0.00" onChange={(e) => setAmount(e.target.value)} disabled={busy} />
              </label>
            </div>
          )}
        </section>

        <aside className="card pay-side form-grid" aria-label="Ödəniş növü və hesab">
          {tab !== 'transfer' && (
            <>
              <label>
                Ödəniş növü
                <select value={method} onChange={(e) => setMethod(e.target.value as Method)} disabled={busy}>
                  {(Object.keys(METHOD_LABEL) as Method[]).map((m) => (
                    <option key={m} value={m}>{METHOD_LABEL[m]}</option>
                  ))}
                </select>
              </label>
              {accountSelect(accountId, setAccountId, usable, 'Hesab')}
            </>
          )}
          <label>
            Qeyd
            <input value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} disabled={busy} />
          </label>
          {tab === 'invoice' && (
            <>
              <button type="button" className="btn-primary" onClick={pay} disabled={busy || !fin}>
                {direction === 'in' ? 'Mədaxil et' : 'Ödə'}
              </button>
              {direction === 'out' && fin && !fin.mismatch && fin.advance !== '0.00' && fin.payable.length > 0 && (
                <button type="button" className="btn-secondary" onClick={fromAdvance} disabled={busy}>
                  Avansdan ödə ({money(fin.advance)})
                </button>
              )}
            </>
          )}
          {tab === 'expense' && (
            <button type="button" className="btn-primary" onClick={expense} disabled={busy}>
              Xərci yaz
            </button>
          )}
          {tab === 'transfer' && (
            <button type="button" className="btn-primary" onClick={transfer} disabled={busy}>
              Köçür
            </button>
          )}
        </aside>
      </div>
    </main>
  );
}
